import React, { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { FiltersService } from '../../services/FiltersService';
import { DROPDOWN_CODES } from '../../constants/dropdown.constants';
import SimpleDropdown from './SimpleDropdown';

interface SkillDropdownProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: 'large' | 'middle' | 'small';
  onDropdownLoad?: () => void; // Callback when dropdown options are loaded
}

const SkillDropdown: React.FC<SkillDropdownProps> = ({
  value,
  onChange,
  placeholder = "Select Skill",
  disabled = false,
  size = 'large',
  onDropdownLoad
}) => {
  const [skillOptions, setSkillOptions] = useState<{ key: string, label: string }[]>([]);
  const [loading, setLoading] = useState(false);

  const loadSkillOptions = useCallback(async () => {
    setLoading(true);
    try {
      const skills = await FiltersService.getDropdownItems(DROPDOWN_CODES.Skill);
      setSkillOptions(skills.map(item => ({
        key: (item.value || '').trim().toUpperCase(),
        label: item.label || ''
      })));
      if (onDropdownLoad) onDropdownLoad();
    } catch (error) {
      console.error('Failed to load skill options:', error);
      message.error('Failed to load skill options');
      if (onDropdownLoad) onDropdownLoad();
    } finally {
      setLoading(false);
    }
  }, [onDropdownLoad]);

  useEffect(() => {
    loadSkillOptions();
  }, [loadSkillOptions]);

  const handleSkillChange = (skillCode: string) => {
    onChange(skillCode);
  };

  // DEBUG LOG for dropdown value debugging
  console.log('SkillDropdown value:', value, 'options:', skillOptions);

  return (
    <SimpleDropdown
      options={skillOptions}
      value={value}
      onChange={handleSkillChange}
      placeholder={loading ? "Loading..." : placeholder}
      disabled={disabled || loading}
      size={size}
    />
  );
};

export default SkillDropdown; 